import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Loader2, Wand2, Bell, X, ExternalLink, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function Followups() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const res = await api.get("/followups");
    setItems(res.data.followups);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const draft = async (f) => {
    setBusy(`draft-${f.id}`);
    try {
      const res = await api.post(`/followups/${f.id}/draft`, {});
      setDrafts((d) => ({ ...d, [f.id]: res.data.draft }));
    } catch { toast.error("Brouillon impossible"); } finally { setBusy(""); }
  };
  const remind = async (f) => {
    setBusy(`remind-${f.id}`);
    try { await api.post(`/followups/${f.id}/remind`); toast.success("Tâche de relance ajoutée à Aujourd'hui"); }
    catch { toast.error("Erreur"); } finally { setBusy(""); }
  };
  const ignore = async (f) => {
    setItems((prev) => prev.filter((x) => x.id !== f.id));
    await api.post(`/followups/${f.id}/ignore`);
    toast.success("Relance ignorée");
  };

  const stale = items.filter((i) => i.days >= 5);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">Relances</h1>
        <p className="text-muted-foreground mt-1 text-sm">Ce que tu attends de quelqu'un. AIRTIME prépare une relance pour tout contact silencieux depuis 5 jours ou plus.</p>
      </div>

      {stale.length > 0 && (
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4 flex items-center gap-3" data-testid="followups-stale-banner">
          <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0" />
          <div className="text-sm text-amber-100">{stale.length} contact(s) sans réponse depuis plus de 5 jours — une relance est recommandée.</div>
        </div>
      )}

      {loading ? (
        <div className="h-[40vh] flex items-center justify-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          Tu n'attends de réponse de personne. Rien à relancer.
        </div>
      ) : (
        <div className="space-y-3" data-testid="followups-list">
          {items.map((f) => {
            const isStale = f.days >= 5;
            return (
              <motion.div key={f.id} layout initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                data-testid={`followup-${f.id}`}
                className={`rounded-2xl border p-5 ${isStale ? "border-amber-500/40 bg-amber-500/5" : "border-border bg-card"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">{f.project && f.project !== "Autre" ? f.project : f.from}</span>
                      <span className={`font-mono text-[10px] px-2 py-0.5 rounded-full ${isStale ? "text-amber-300 bg-amber-500/10" : "text-muted-foreground bg-muted"}`}>
                        {f.days} j sans réponse
                      </span>
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">{f.waiting_for || f.subject}</div>
                    <div className="text-xs text-muted-foreground/70 mt-0.5">De {f.from}</div>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-2.5">
                  <button data-testid={`followup-remind-${f.id}`} onClick={() => remind(f)} disabled={busy === `remind-${f.id}`}
                    className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-medium hover:bg-accent transition-all disabled:opacity-60">
                    {busy === `remind-${f.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />} Me le rappeler
                  </button>
                  <button data-testid={`followup-draft-${f.id}`} onClick={() => draft(f)} disabled={busy === `draft-${f.id}`}
                    className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm text-primary-foreground font-medium hover:opacity-90 transition-all disabled:opacity-60">
                    {busy === `draft-${f.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Rédiger une relance
                  </button>
                  <button data-testid={`followup-ignore-${f.id}`} onClick={() => ignore(f)}
                    className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-accent transition-all">
                    <X className="w-4 h-4" /> Ignorer
                  </button>
                </div>

                {drafts[f.id] && (
                  <div className="mt-4">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
                      <Check className="w-3 h-3 text-emerald-400" /> Relance proposée — à valider avant envoi
                    </div>
                    <textarea data-testid={`followup-draft-text-${f.id}`} value={drafts[f.id]}
                      onChange={(e) => setDrafts((d) => ({ ...d, [f.id]: e.target.value }))}
                      className="w-full h-40 rounded-xl bg-secondary border border-border p-3.5 text-sm resize-none airtime-scroll" />
                    <a href={`mailto:?subject=RE: ${encodeURIComponent(f.subject || "")}&body=${encodeURIComponent(drafts[f.id])}`}
                      className="mt-2 inline-flex items-center gap-2 text-xs text-primary hover:underline">
                      <ExternalLink className="w-3 h-3" /> Ouvrir dans Mail
                    </a>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
